import axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios';
import { supabase } from './supabaseClient';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

// Create a flag to prevent multiple refresh attempts
let isRefreshing = false;
// Queue of requests that should be retried after token refresh
let refreshQueue: Array<{
  resolve: (token: string) => void;
  reject: (error: any) => void;
}> = [];

// Process the queue of failed requests
const processQueue = (error: any, token: string | null = null) => {
  refreshQueue.forEach(promise => {
    if (error) {
      promise.reject(error);
    } else if (token) {
      promise.resolve(token);
    }
  });
  
  refreshQueue = [];
};

const axiosInstance = axios.create({
  baseURL: API_URL,
});

// Add auth interceptor to include access tokens in requests
axiosInstance.interceptors.request.use(async (config) => {
  const { data: { session } } = await supabase.auth.getSession();
  if (session?.access_token) {
    config.headers.Authorization = `Bearer ${session.access_token}`;
  }
  return config;
});

// Add response interceptor to handle token refresh
axiosInstance.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as AxiosRequestConfig & { _retry?: boolean };
    
    // If the error is not 401 or the request has already been retried, reject it
    if (!error.response || error.response.status !== 401 || originalRequest._retry) {
      return Promise.reject(error);
    }

    // If we're already refreshing, add this request to the queue
    if (isRefreshing) {
      return new Promise<AxiosResponse>((resolve, reject) => {
        refreshQueue.push({
          resolve: (token: string) => {
            originalRequest.headers = {
              ...originalRequest.headers,
              Authorization: `Bearer ${token}`
            };
            resolve(axios(originalRequest));
          },
          reject: (err) => {
            reject(err);
          }
        });
      });
    }

    originalRequest._retry = true;
    isRefreshing = true;

    try {
      // Attempt to refresh the session
      const { data, error } = await supabase.auth.refreshSession();
      
      if (error || !data.session) {
        // If refresh fails, signout the user and reject all queued requests
        await supabase.auth.signOut();
        processQueue(error || new Error('Failed to refresh token'));
        
        // Redirect to login page
        window.location.href = '/login';
        return Promise.reject(error || new Error('Failed to refresh token'));
      }

      // Session refreshed successfully
      const newToken = data.session.access_token;
      
      // Update the original request and process queue with new token
      originalRequest.headers = {
        ...originalRequest.headers,
        Authorization: `Bearer ${newToken}`
      };
      
      processQueue(null, newToken);
      
      return axios(originalRequest);
    } catch (refreshError) {
      processQueue(refreshError);
      return Promise.reject(refreshError);
    } finally {
      isRefreshing = false;
    }
  }
);

export default axiosInstance;
